import { TRADE_ENTRY_SOURCES } from "../trades/tradeUtils";

const present = (value) => value !== undefined && value !== null && value !== "";

const levelText = (value) => present(value) ? value : "-";

const listText = (values) => Array.isArray(values) && values.length > 0
  ? values.join(" | ")
  : "-";

export function selectExecutableEvaluationPlan(result) {
  const evaluation = result?.evaluation || result || {};
  const requestedSide = result?.requestedSide || evaluation?._sharedRecord?.requestedSide || "BOTH";
  if (evaluation.verdict === "REJECT") return null;
  const candidates = evaluation.responseMode === "CHART_COMPACT"
    ? [evaluation.shortTerm, evaluation.swing]
    : evaluation.details
      ? [evaluation.details.intradayPlan, evaluation.details.swingPlan]
      : [evaluation];
  const positive = (value) => typeof value === "number" && Number.isFinite(value) && value > 0;
  return candidates.filter(Boolean).map((plan) => ({
    ...plan,
    side: plan.side || plan.bias,
    timing: plan.timing || plan.status,
    entry: plan.entry ?? plan.entryPrice,
    takeProfit1: plan.takeProfit1 ?? plan.targets?.[0]?.price,
    takeProfit2: plan.takeProfit2 ?? plan.targets?.[1]?.price,
  })).filter((plan) => {
    if (!["LONG", "SHORT"].includes(plan.side) || plan.timing !== "ENTER_NOW"
      || (["LONG", "SHORT"].includes(requestedSide) && plan.side !== requestedSide)
      || ![plan.entry, plan.stopLoss, plan.takeProfit1].every(positive)) return false;
    const sign = plan.side === "LONG" ? 1 : -1;
    return (plan.entry - plan.stopLoss) * sign > 0 && (plan.takeProfit1 - plan.entry) * sign > 0
      && (plan.takeProfit2 == null || (positive(plan.takeProfit2) && (plan.takeProfit2 - plan.takeProfit1) * sign > 0));
  }).sort((left, right) => (Number(right.score) || 0) - (Number(left.score) || 0))[0] || null;
}

export function buildChartEvaluationTradePrefill(result) {
  const evaluation = result?.evaluation || result || {};
  const model = result?.model || evaluation.model || "unknown model";
  const verdict = evaluation.verdict || "UNRATED";
  const score = present(evaluation.score) ? `${evaluation.score}/100` : "unscored";
  const timing = String(evaluation.timing || "UNSPECIFIED").replaceAll("_", " ");

  const comment = [
    `AI chart evaluation - ${model}`,
    `${verdict} ${score} - ${evaluation.side || "NONE"} - ${timing}`,
    `Entry ${levelText(evaluation.entry)} | SL ${levelText(evaluation.stopLoss)} | TP1 ${levelText(evaluation.takeProfit1)} | TP2 ${levelText(evaluation.takeProfit2)}`,
    `Rationale: ${evaluation.summary || "-"}`,
    `Strengths: ${listText(evaluation.strengths)}`,
    `Risks: ${listText(evaluation.risks)}`
  ].join("\n");

  return {
    tradeSide: evaluation.side,
    price: present(evaluation.entry) ? evaluation.entry : null,
    stopLoss: present(evaluation.stopLoss) ? evaluation.stopLoss : null,
    takeProfit: present(evaluation.takeProfit1) ? evaluation.takeProfit1 : null,
    takeProfit2: present(evaluation.takeProfit2) ? evaluation.takeProfit2 : null,
    entrySource: TRADE_ENTRY_SOURCES.ABSOLUTE_PRICE,
    entrySelector: null,
    comment
  };
}
